import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface SliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  thumbContent?: React.ReactNode;
  thumbTooltip?: React.ReactNode;
  thumbLabel?: React.ReactNode;
  classNameTrack?: string;
  customTooltip?: boolean;
}

const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    {
      className,
      classNameTrack,
      thumbContent,
      thumbTooltip,
      thumbLabel,
      customTooltip = true,
      ...props
    },
    ref
  ) => (
    <SliderPrimitive.Root
      ref={ref}
      className={cn('relative flex w-full touch-none select-none items-center', className)}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          'slider-track-custom relative h-1 w-full grow overflow-hidden rounded-full',
          classNameTrack
        )}
      >
        <SliderPrimitive.Range className="bg-primary absolute h-full" />
      </SliderPrimitive.Track>
      {customTooltip && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <SliderPrimitive.Thumb className="slider-thumb-custom border-primary bg-background ring-offset-background focus-visible:ring-ring block h-5 w-5 rounded-full border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50">
                {thumbContent}
                {thumbLabel && (
                  <div className="absolute -bottom-8 left-1/2 z-10 -translate-x-1/2 transform">
                    {thumbLabel}
                  </div>
                )}
              </SliderPrimitive.Thumb>
            </TooltipTrigger>
            <TooltipContent className="overflow-visible bg-white p-0">
              <div className="relative">
                {thumbTooltip}
                <div
                  className="bg-popover absolute h-2 w-2 rotate-45 transform bg-white"
                  style={{
                    bottom: '-4px',
                    left: '50%',
                    transform: 'translateX(-50%) rotate(45deg)'
                  }}
                />
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {!customTooltip && (
        <SliderPrimitive.Thumb className="slider-thumb-custom border-primary bg-background ring-offset-background focus-visible:ring-ring block h-5 w-5 rounded-full border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50">
          {thumbContent}
          {thumbTooltip && (
            <div className="absolute -top-8 left-1/2 z-10 -translate-x-1/2 transform">
              {thumbTooltip}
            </div>
          )}
          {thumbLabel && (
            <div className="absolute -bottom-8 left-1/2 z-10 -translate-x-1/2 transform">
              {thumbLabel}
            </div>
          )}
        </SliderPrimitive.Thumb>
      )}
    </SliderPrimitive.Root>
  )
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
